/**
 * Liberação de operação restrita (hoje: alterar setores com a portaria aberta).
 *
 * Servidor primeiro, porque ele é a verdade sobre quem tem acesso agora. Sem
 * rede — que é o normal em portaria de galpão — cai na lista baixada antes,
 * comparando o resumo da senha. Nunca guardamos a senha em si.
 */

import {
  baixarUsuariosAutorizados,
  ErroApi,
  NIVEL_SUPERVISOR,
  verificarAcesso,
  type Autorizacao,
} from './api/cliente';
import {
  buscarUsuarioAutorizado,
  salvarUsuariosAutorizados,
  totalUsuariosAutorizados,
} from './dados/repositorio';
import {resumoDeSenha} from './dominio/hash';
import {idDoAparelho} from './sessao';

export type ResultadoAutorizacao = Autorizacao & {
  /** true quando a conferência foi feita na lista local, sem servidor. */
  offline?: boolean;
};

/**
 * Atualiza a lista de quem pode liberar. Melhor esforço: chamada no preparo do
 * evento, quando ainda há rede, para que a portaria não dependa dela depois.
 */
export async function atualizarUsuariosAutorizados(): Promise<number> {
  const doServidor = await baixarUsuariosAutorizados();

  await salvarUsuariosAutorizados(
    doServidor.map(u => ({
      remoteId: u.remoteId,
      nome: u.nome,
      nivel: u.nivel,
      senhaHash: resumoDeSenha(u.remoteId, u.senha),
    })),
  );

  return doServidor.length;
}

async function conferirLocalmente(
  usuarioId: string,
  senha: string,
): Promise<ResultadoAutorizacao> {
  const guardados = await totalUsuariosAutorizados();
  if (guardados === 0) {
    return {
      autorizado: false,
      offline: true,
      motivo:
        'Sem rede e sem lista de acesso neste aparelho. Conecte à internet uma vez para liberar offline depois.',
    };
  }

  const usuario = await buscarUsuarioAutorizado(Number(usuarioId));
  if (!usuario) {
    return {autorizado: false, offline: true, motivo: 'ID ou senha inválido'};
  }

  if (usuario.senhaHash !== resumoDeSenha(usuario.remoteId, senha)) {
    return {autorizado: false, offline: true, motivo: 'ID ou senha inválido'};
  }

  if (usuario.nivel <= NIVEL_SUPERVISOR) {
    return {
      autorizado: false,
      offline: true,
      nome: usuario.nome,
      nivel: usuario.nivel,
      motivo: `${usuario.nome} não tem acesso para alterar setores`,
    };
  }

  return {autorizado: true, offline: true, nome: usuario.nome, nivel: usuario.nivel};
}

export async function autorizar(
  usuarioId: string,
  senha: string,
): Promise<ResultadoAutorizacao> {
  const aparelho = await idDoAparelho();

  try {
    return await verificarAcesso(usuarioId, senha, aparelho);
  } catch (erro) {
    // Só a falha de comunicação justifica a reserva local. Resposta do servidor
    // dizendo "não autorizado" é resposta, e vale.
    if (erro instanceof ErroApi) {
      return conferirLocalmente(usuarioId, senha);
    }
    throw erro;
  }
}
